import { useState } from "react";
import type { StudentWorkspaceSnapshot } from "../shared/domain/index.js";
import type { AppSession } from "./useSession.js";

export function StudentProfileView({
  session,
  snapshot
}: {
  session: AppSession;
  snapshot: StudentWorkspaceSnapshot;
}) {
  const [shareLink, setShareLink] = useState<string>("");
  const [linkedInUrl, setLinkedInUrl] = useState<string>("");
  const [status, setStatus] = useState<string>("");
  const [loadingShare, setLoadingShare] = useState(false);
  const badge = snapshot.badge;
  const active = badge.active;
  const joinedAt = snapshot.profile.createdAt || new Date().toLocaleDateString("nl-NL");

  async function createShareLink() {
    setLoadingShare(true);
    setStatus("");
    try {
      const response = await fetch("/api/v2/student/badge/share-link", {
        credentials: "include"
      });
      const payload = await response.json() as {
        url?: string;
        linkedInShareUrl?: string;
        error?: string;
      };
      if (!response.ok || !payload.url) {
        setStatus(payload.error === "badge_not_unlocked" ? "Nog geen badge vrijgespeeld." : "Share-link maken is niet gelukt.");
        return;
      }
      setShareLink(payload.url);
      setLinkedInUrl(payload.linkedInShareUrl ?? "");
      setStatus("Share-link is klaar en 15 dagen geldig.");
    } catch {
      setStatus("Share-link maken is tijdelijk niet beschikbaar.");
    } finally {
      setLoadingShare(false);
    }
  }

  async function copyShareLink() {
    if (!shareLink) return;
    try {
      await navigator.clipboard.writeText(shareLink);
      setStatus("Link gekopieerd.");
    } catch {
      setStatus("Kopieren lukte niet, gebruik handmatig selecteren.");
    }
  }

  return (
    <section className="fd-section" id="student-profile-page">
      <header className="fd-page-header">
        <h1>Student profiel</h1>
        <p>Accountgegevens, badges en certificaatstatus binnen FirstDesk v2.</p>
      </header>

      <div className="fd-grid fd-grid--2">
        <article className="fd-panel">
          <h2 className="fd-panel-title">Account</h2>
          <div className="fd-profile-account-list--v2">
            <div className="fd-profile-account-row"><span className="fd-profile-label">Naam</span><span className="fd-profile-value">{snapshot.profile.name || session.displayName}</span></div>
            <div className="fd-profile-account-row"><span className="fd-profile-label">E-mail</span><span className="fd-profile-value">{snapshot.profile.email || session.email}</span></div>
            <div className="fd-profile-account-row"><span className="fd-profile-label">Rol</span><span className="fd-profile-value">Student</span></div>
            <div className="fd-profile-account-row"><span className="fd-profile-label">Instelling</span><span className="fd-profile-value">{snapshot.profile.institutionName || "Onbekend"}</span></div>
            <div className="fd-profile-account-row"><span className="fd-profile-label">Lid sinds</span><span className="fd-profile-value">{joinedAt}</span></div>
          </div>
        </article>

        <article className="fd-panel">
          <h2 className="fd-panel-title">Badge status</h2>
          {active ? (
            <>
              <div className="fd-student-level-card">
                <span>Behaald niveau</span>
                <strong>{active.title}</strong>
                <small>{badge.completedTickets} afgeronde tickets</small>
              </div>
              <p className="fd-muted-copy">{badge.next ? `Nog ${badge.next.remaining} tickets tot ${badge.next.title}.` : "Alle badgelevels vrijgespeeld."}</p>
              <div className="fd-profile-share-actions">
                <button className="fd-button fd-button--secondary fd-button--small" type="button" onClick={createShareLink} disabled={loadingShare}>
                  {loadingShare ? "Link maken..." : "Genereer share-link"}
                </button>
                <a className="fd-button fd-button--small" href="/student/profile/badge/certificate.pdf" target="_blank" rel="noreferrer">Open certificaat (PDF)</a>
              </div>
              {shareLink ? (
                <div className="fd-profile-share-box">
                  <input readOnly value={shareLink} aria-label="Badge share link" />
                  <button className="fd-button fd-button--secondary fd-button--small" type="button" onClick={copyShareLink}>Kopieer</button>
                  {linkedInUrl ? <a className="fd-button fd-button--secondary fd-button--small" href={linkedInUrl} target="_blank" rel="noreferrer">Deel op LinkedIn</a> : null}
                </div>
              ) : null}
            </>
          ) : (
            <p>Nog geen badge vrijgespeeld. Rond tickets af om je eerste certificaatniveau te unlocken.</p>
          )}
          {status ? <p className="fd-muted-copy">{status}</p> : null}
        </article>
      </div>
    </section>
  );
}
